import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Select, Segmented, Spin, Statistic, Table, Typography } from 'antd';
import api from '../api';
import { hrsToDuration } from '../utils/time';

const { Title } = Typography;
const { Option } = Select;

// Rolls over to hours past 60 minutes; see utils/time.
const hrsToTime = (hrs) => hrsToDuration(hrs) ?? '-';

function formatMonthKey(mk) {
  if (!mk) return mk;
  const [y, m] = mk.split('-');
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${labels[parseInt(m, 10) - 1]} ${y}`;
}

// Shared pivot cell renderer — reusable for both Agent Pivot and Campaign Trends
function PivotCell({ row }) {
  if (!row) return <span style={{ color: 'var(--ant-color-text-quaternary)' }}>-</span>;
  const lbl = t => <span style={{ color: 'var(--ant-color-text-secondary)' }}>{t}</span>;
  return (
    <div style={{ fontSize: 11, lineHeight: 1.7, textAlign: 'left' }}>
      <div>{lbl('Total:')}{' '}{row.TotalContactsHandled?.toLocaleString() ?? '-'}</div>
      <div>{lbl('ACW (All):')}{' '}{hrsToTime(row.AvgACWHrs)}</div>
      <div>{lbl('Add ACW (All):')}{' '}{hrsToTime(row.AvgAdditionalACWHrs)}</div>
      <div>{lbl('Talk (All):')}{' '}{hrsToTime(row.AvgAgentInteractionHoldHrs)}</div>
      <div>{lbl('AHT (All):')}{' '}{hrsToTime(row.AvgHandleHrs)}</div>
      <div>{lbl('In:')}{' '}{row.ContactsHandledIncoming?.toLocaleString() ?? '-'}</div>
      <div>{lbl('Out:')}{' '}{row.ContactsHandledOutbound?.toLocaleString() ?? '-'}</div>
      <div>{lbl('CB:')}{' '}{row.CallbackContactsHandled?.toLocaleString() ?? '-'}</div>
    </div>
  );
}

export default function CNEMonthlyStatsPage() {
  const [months, setMonths] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [viewMode, setViewMode] = useState('Agent Table');
  const [fromMonth, setFromMonth] = useState(() => {
    const now = new Date();
    let y = now.getFullYear(), m = now.getMonth() + 1 - 3;
    if (m <= 0) { m += 12; y -= 1; }
    return `${y}-${String(m).padStart(2, '0')}`;
  });
  const [toMonth, setToMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    const monthsReq = api.get('/api/cne-stats/months').then(r => {
      const m = r.data || [];
      setMonths(m);
      if (m.length > 0) setSelectedMonth(m[0]);
    });
    const dataReq = api.get('/api/cne-stats').then(r => {
      setData(r.data || []);
    });
    Promise.all([monthsReq, dataReq]).finally(() => setLoading(false));
  }, []);

  // From and To default to dates off the clock, but the dropdowns only offer
  // months that have uploaded stats. The current month has no row until it is
  // uploaded, so To held a value no Option matched and Ant fell back to
  // printing the raw key -- 2026-09 sitting beside a formatted "Jun 2026".
  //
  // months is newest first. An out-of-range To becomes the newest month there
  // is, and an out-of-range From falls back three months from it, which is the
  // window the defaults were reaching for in the first place.
  useEffect(() => {
    if (!months.length) return;
    setToMonth(t => (months.includes(t) ? t : months[0]));
    setFromMonth(f => (months.includes(f) ? f : months[Math.min(3, months.length - 1)]));
  }, [months]);

  const campaigns = useMemo(() => (
    [...new Set(data.map(r => r.CampaignName).filter(Boolean))].sort()
  ), [data]);

  const campaignFilteredData = useMemo(() => (
    selectedCampaign ? data.filter(r => r.CampaignName === selectedCampaign) : data
  ), [data, selectedCampaign]);

  const filteredData = useMemo(() => (
    selectedMonth ? campaignFilteredData.filter(r => r.MonthKey === selectedMonth) : campaignFilteredData
  ), [campaignFilteredData, selectedMonth]);

  const pivotRangeData = useMemo(() => (
    campaignFilteredData.filter(r => r.MonthKey >= fromMonth && r.MonthKey <= toMonth)
  ), [campaignFilteredData, fromMonth, toMonth]);

  const isPivotMode = viewMode === 'Agent Pivot' || viewMode === 'Campaign Trends';
  const scorecardSource = isPivotMode ? pivotRangeData : filteredData;

  const scorecard = useMemo(() => {
    if (!scorecardSource.length) return null;
    const inbound   = scorecardSource.reduce((s, r) => s + (r.ContactsHandledIncoming  || 0), 0);
    const outbound  = scorecardSource.reduce((s, r) => s + (r.ContactsHandledOutbound   || 0), 0);
    const callbacks = scorecardSource.reduce((s, r) => s + (r.CallbackContactsHandled   || 0), 0);
    const acwRows   = scorecardSource.filter(r => r.AvgACWHrs != null);
    const addRows   = scorecardSource.filter(r => r.AvgAdditionalACWHrs != null);
    const talkRows  = scorecardSource.filter(r => r.AvgAgentInteractionHoldHrs != null);
    const htRows    = scorecardSource.filter(r => r.AvgHandleHrs != null);
    const avgACW    = acwRows.length  ? acwRows.reduce((s, r)  => s + Number(r.AvgACWHrs                || 0), 0) / acwRows.length  : null;
    const avgAddACW = addRows.length  ? addRows.reduce((s, r)  => s + Number(r.AvgAdditionalACWHrs      || 0), 0) / addRows.length  : null;
    const avgTalk   = talkRows.length ? talkRows.reduce((s, r) => s + Number(r.AvgAgentInteractionHoldHrs || 0), 0) / talkRows.length : null;
    const avgHT     = htRows.length   ? htRows.reduce((s, r)   => s + Number(r.AvgHandleHrs             || 0), 0) / htRows.length   : null;
    return { inbound, outbound, callbacks, avgACW, avgAddACW, avgTalk, avgHT };
  }, [scorecardSource]);

  // Agent Pivot
  const { pivotMonths, pivotRows } = useMemo(() => {
    const mSet = [...new Set(pivotRangeData.map(r => r.MonthKey))].sort();
    const agentMap = {};
    pivotRangeData.forEach(r => {
      const key = r.AWSID || r.DisplayName;
      if (!agentMap[key]) agentMap[key] = { name: r.DisplayName, awsid: r.AWSID, months: {} };
      agentMap[key].months[r.MonthKey] = r;
    });
    return { pivotMonths: mSet, pivotRows: Object.values(agentMap).sort((a, b) => a.name.localeCompare(b.name)) };
  }, [pivotRangeData]);

  // Campaign Trends pivot — aggregate agent rows per campaign+month
  const { campaignPivotMonths, campaignPivotRows } = useMemo(() => {
    const mSet = [...new Set(pivotRangeData.map(r => r.MonthKey))].sort();
    const campMap = {};
    pivotRangeData.forEach(r => {
      const camp = r.CampaignName || 'Unknown';
      if (!campMap[camp]) campMap[camp] = { name: camp, months: {} };
      const mk = r.MonthKey;
      if (!campMap[camp].months[mk]) {
        campMap[camp].months[mk] = {
          TotalContactsHandled: 0, ContactsHandledIncoming: 0,
          ContactsHandledOutbound: 0, CallbackContactsHandled: 0,
          _acw: [], _addAcw: [], _talk: [], _aht: [],
        };
      }
      const cell = campMap[camp].months[mk];
      cell.TotalContactsHandled      += (r.TotalContactsHandled      || 0);
      cell.ContactsHandledIncoming   += (r.ContactsHandledIncoming   || 0);
      cell.ContactsHandledOutbound   += (r.ContactsHandledOutbound   || 0);
      cell.CallbackContactsHandled   += (r.CallbackContactsHandled   || 0);
      if (r.AvgACWHrs != null)                cell._acw.push(Number(r.AvgACWHrs                || 0));
      if (r.AvgAdditionalACWHrs != null)      cell._addAcw.push(Number(r.AvgAdditionalACWHrs   || 0));
      if (r.AvgAgentInteractionHoldHrs != null) cell._talk.push(Number(r.AvgAgentInteractionHoldHrs || 0));
      if (r.AvgHandleHrs != null)             cell._aht.push(Number(r.AvgHandleHrs             || 0));
    });
    // Resolve averages
    Object.values(campMap).forEach(c => {
      Object.values(c.months).forEach(cell => {
        cell.AvgACWHrs                = cell._acw.length   ? cell._acw.reduce((s, v) => s + v, 0)   / cell._acw.length   : null;
        cell.AvgAdditionalACWHrs      = cell._addAcw.length ? cell._addAcw.reduce((s, v) => s + v, 0) / cell._addAcw.length : null;
        cell.AvgAgentInteractionHoldHrs = cell._talk.length ? cell._talk.reduce((s, v) => s + v, 0)  / cell._talk.length  : null;
        cell.AvgHandleHrs             = cell._aht.length   ? cell._aht.reduce((s, v) => s + v, 0)   / cell._aht.length   : null;
      });
    });
    return {
      campaignPivotMonths: mSet,
      campaignPivotRows: Object.values(campMap).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [pivotRangeData]);

  const makePivotMonthCols = (months, firstCol) => [
    firstCol,
    ...months.map(m => ({
      title: formatMonthKey(m),
      key: m,
      align: 'center',
      width: 155,
      render: (_, r) => <PivotCell row={r.months[m]} />,
    })),
  ];

  const agentPivotColumns  = makePivotMonthCols(pivotMonths, {
    title: 'Agent', key: 'agent', fixed: 'left', width: 180,
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (_, r) => r.name,
  });
  const campaignPivotColumns = makePivotMonthCols(campaignPivotMonths, {
    title: 'Campaign', key: 'campaign', fixed: 'left', width: 180,
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (_, r) => r.name,
  });

  const tableColumns = [
    { title: 'Agent',    dataIndex: 'DisplayName',  key: 'agent',    sorter: (a, b) => a.DisplayName.localeCompare(b.DisplayName) },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'campaign', sorter: (a, b) => a.CampaignName.localeCompare(b.CampaignName) },
    { title: 'Contacts Handled', dataIndex: 'TotalContactsHandled',    key: 'total',     align: 'right', sorter: (a, b) => a.TotalContactsHandled - b.TotalContactsHandled, render: v => v?.toLocaleString() ?? '-' },
    { title: 'Avg ACW (All)',      key: 'avgacw',    align: 'right', sorter: (a, b) => (a.AvgACWHrs || 0) - (b.AvgACWHrs || 0), render: (_, r) => hrsToTime(r.AvgACWHrs) },
    { title: 'Avg Add. ACW (All)', key: 'avgaddacw', align: 'right', sorter: (a, b) => (a.AvgAdditionalACWHrs || 0) - (b.AvgAdditionalACWHrs || 0), render: (_, r) => hrsToTime(r.AvgAdditionalACWHrs) },
    { title: 'Avg Talk Time (All)',key: 'avgtalk',   align: 'right', sorter: (a, b) => (a.AvgAgentInteractionHoldHrs || 0) - (b.AvgAgentInteractionHoldHrs || 0), render: (_, r) => hrsToTime(r.AvgAgentInteractionHoldHrs) },
    { title: 'Avg AHT (All)',      key: 'avght',     align: 'right', sorter: (a, b) => (a.AvgHandleHrs || 0) - (b.AvgHandleHrs || 0), render: (_, r) => hrsToTime(r.AvgHandleHrs) },
    { title: 'Inbound',   dataIndex: 'ContactsHandledIncoming', key: 'inbound',   align: 'right', sorter: (a, b) => a.ContactsHandledIncoming - b.ContactsHandledIncoming, render: v => v?.toLocaleString() ?? '-' },
    { title: 'Outbound',  dataIndex: 'ContactsHandledOutbound', key: 'outbound',  align: 'right', sorter: (a, b) => a.ContactsHandledOutbound - b.ContactsHandledOutbound, render: v => v?.toLocaleString() ?? '-' },
    { title: 'Callbacks', dataIndex: 'CallbackContactsHandled', key: 'callbacks', align: 'right', sorter: (a, b) => a.CallbackContactsHandled - b.CallbackContactsHandled, render: v => v?.toLocaleString() ?? '-' },
  ];

  const showFromTo = isPivotMode;

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>Campaign CNE Monthly Stats</Title>

      <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Select value={selectedCampaign || ''} onChange={v => setSelectedCampaign(v)} style={{ width: 200 }} placeholder="All Campaigns">
            <Option value="">All Campaigns</Option>
            {campaigns.map(c => <Option key={c} value={c}>{c}</Option>)}
          </Select>
        </Col>
        {viewMode === 'Agent Table' && (
          <Col>
            <Select value={selectedMonth || ''} onChange={v => setSelectedMonth(v)} style={{ width: 160 }} placeholder="Select month">
              <Option value="">All Months</Option>
              {months.map(m => <Option key={m} value={m}>{formatMonthKey(m)}</Option>)}
            </Select>
          </Col>
        )}
        {showFromTo && (
          <>
            <Col>
              <span style={{ marginRight: 6, fontSize: 13 }}>From</span>
              <Select value={fromMonth} onChange={v => setFromMonth(v)} style={{ width: 130 }}>
                {months.slice().reverse().map(m => (
                  <Option key={m} value={m} disabled={m > toMonth}>{formatMonthKey(m)}</Option>
                ))}
              </Select>
            </Col>
            <Col>
              <span style={{ marginRight: 6, fontSize: 13 }}>To</span>
              <Select value={toMonth} onChange={v => setToMonth(v)} style={{ width: 130 }}>
                {months.slice().reverse().map(m => (
                  <Option key={m} value={m} disabled={m < fromMonth}>{formatMonthKey(m)}</Option>
                ))}
              </Select>
            </Col>
          </>
        )}
        <Col>
          <Segmented options={['Agent Table', 'Agent Pivot', 'Campaign Trends']} value={viewMode} onChange={setViewMode} />
        </Col>
      </Row>

      {scorecard && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { title: 'Inbound Calls',        value: scorecard.inbound },
            { title: 'Outbound Calls',       value: scorecard.outbound },
            { title: 'Callbacks',            value: scorecard.callbacks },
            { title: 'Avg ACW (All)',        value: hrsToTime(scorecard.avgACW) },
            { title: 'Avg Add. ACW (All)',   value: hrsToTime(scorecard.avgAddACW) },
            { title: 'Avg Talk Time (All)',  value: hrsToTime(scorecard.avgTalk) },
            { title: 'Avg AHT (All)',        value: hrsToTime(scorecard.avgHT) },
          ].map((s) => (
            <Card key={s.title} size="small" variant="outlined" style={{ flex: '1 1 0', minWidth: 100 }}>
              <Statistic title={s.title} value={s.value} />
            </Card>
          ))}
        </div>
      )}

      <Spin spinning={loading}>
        {viewMode === 'Agent Table' && (
          <Table size="small" dataSource={filteredData} columns={tableColumns}
            rowKey={(r, i) => `${r.MonthKey}-${r.AWSID || ''}-${i}`}
            pagination={{ defaultPageSize: 50, showSizeChanger: false }} />
        )}
        {viewMode === 'Agent Pivot' && (
          <Table size="small" dataSource={pivotRows} columns={agentPivotColumns}
            rowKey={r => r.awsid || r.name} pagination={false} scroll={{ x: 'max-content' }} />
        )}
        {viewMode === 'Campaign Trends' && (
          <Table size="small" dataSource={campaignPivotRows} columns={campaignPivotColumns}
            rowKey={r => r.name} pagination={false} scroll={{ x: 'max-content' }} />
        )}
      </Spin>
    </div>
  );
}
